import {List, Map} from 'immutable';
import cn from 'classnames';
import React, {useMemo, useRef, useState, useEffect} from 'react';
import {Topic, TopicFilter, TopicId} from '@store/topic-keyword/TopicKeyword.types';
import './CallCardSidebar.sass';
import {KeywordTopicSidebar} from '@components/call-card/KeywordTopicSidebar';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import {Speaker, TopicMatch, TranscriptBlock} from '@store/call-card/CallCard.types';
import {NarrowTranscript} from '@components/call-card/NarrowTranscript';
import {Color, Seconds} from '@store/@types/util';
import {ChevronUpIcon} from '@components/icon/ChevronUpIcon';
import {WideTranscript} from '@components/call-card/WideTranscript';
import IconButton from '@material-ui/core/IconButton';
import {associateBy} from 'utils/associateBy';
import User from 'records/User';
import {PlayerProps, scrollTranscriptToTime} from '@components/call-card/Player.utils';
import {CallCardSource} from '@store/call-card/CallCardSource';
import {CallCardVariant} from '@components/call-card/CallCard.types';
import {FileUploadState} from 'records/FileUploadState';
import {registerEvents} from 'utils/registerEvents';
import {CopyIcon} from '@components/icon/CopyIcon';
import {toast} from '@store/toast/Toast.utils';
import formatSecondsShort from 'utils/formatSecondsShort';
import Tooltip from '@material-ui/core/Tooltip';

interface CallCardSidebarProps extends PlayerProps {
  topics: List<Topic>;
  activeTopics: Map<TopicId, Color>;
  matches: Map<TopicId, List<TopicMatch>>;
  callId: FileUploadId;
  keywordSearch: typeof CallCardSource.keywordSearch;
  transcript: List<TranscriptBlock>;
  speakers: List<Speaker>;
  sessionUser?: User;
  setTimeSeconds: (seconds: Seconds) => void;
  topicFilter: TopicFilter;
  variant: CallCardVariant;
  callState: FileUploadState;
  autoScrollTranscript: boolean;
  setAutoScrollTranscript: (autoScroll: boolean) => void;
}

export const CallCardSidebar: React.FC<CallCardSidebarProps> = ({
  activeTopics,
  matches,
  callId,
  keywordSearch,
  speakers,
  topics,
  transcript,
  sessionUser,
  setTimeSeconds,
  topicFilter,
  variant,
  callState,
  currentTime,
  autoScrollTranscript,
  setAutoScrollTranscript,
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [transcriptRef, setTranscriptRef] = useState<HTMLElement | null>(null);
  const [hideKeywords, setHideKeywords] = useState(false);
  const [hideTranscript, setHideTranscript] = useState(false);
  const [wideTranscript, setWideTranscript] = useState(false);

  const speakersByNumber = useMemo(() => associateBy(speakers, s => s.speakerNumber), [speakers]);
  const isExternal = variant === CallCardVariant.EXTERNAL;

  const rawTextTranscript = useMemo(() => {
    const speakerNames = speakersByNumber.map(s => s?.description ?? 'Unidentified Speaker');
    return transcript
      .map(block => {
        const name = speakerNames.get(block.speakerNumber);
        const timestamp = formatSecondsShort(block.startTime);
        const text = block.chunk;
        return `${name} (${timestamp}): ${text}`;
      })
      .join('\n');
  }, [transcript, speakersByNumber]);

  useEffect(() => {
    if (!transcriptRef) return;

    const handleWheel = (event: WheelEvent) => {
      if (transcriptRef.contains(event.target as Node)) {
        setAutoScrollTranscript(false);
      }
    };

    return registerEvents(document, [['wheel', handleWheel]]);
  }, [transcriptRef, setAutoScrollTranscript]);

  const handleSyncWithPlayback = () => {
    setAutoScrollTranscript(true);
    jumpToTime(currentTime, transcript);
  };

  return (
    <div ref={sidebarRef} className={cn('call-card-sidebar', {wide: wideTranscript})}>
      <IconButton className="toggle-wide" onClick={() => setWideTranscript(!wideTranscript)}>
        <ChevronUpIcon tooltip={wideTranscript ? 'Collapse' : 'Expand'} />
      </IconButton>
      {!isExternal && (
        <>
          <div className="header" onClick={() => setHideKeywords(!hideKeywords)}>
            <h2>Keywords & Topics</h2>
            <ChevronUpIcon
              className={cn({hidden: hideKeywords})}
              tooltip={hideKeywords ? 'Expand' : 'Hide'}
            />
          </div>
          <KeywordTopicSidebar
            hidden={hideKeywords}
            topics={topics}
            activeTopics={activeTopics}
            callId={callId}
            keywordSearch={keywordSearch}
            speakers={speakers}
            topicFilter={topicFilter}
          />
        </>
      )}
      <div
        className="transcript-header header"
        onClick={() => {
          if (!isExternal) {
            setHideTranscript(!hideTranscript);
          }
        }}>
        <h2>
          Transcript
          <Tooltip title="Copy transcript to clipboard">
            <IconButton
              size="small"
              className="copy-button"
              onClick={e => {
                e.stopPropagation();
                navigator.clipboard.writeText(rawTextTranscript);
                toast('Copied transcript to clipboard!');
              }}>
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </h2>
        {!isExternal && (
          <ChevronUpIcon
            className={cn({hidden: hideTranscript})}
            tooltip={hideTranscript ? 'Expand' : 'Hide'}
          />
        )}
      </div>
      {!autoScrollTranscript && !hideTranscript && (
        <div className="sync-playback-container">
          <button className="sync-playback-button" onClick={handleSyncWithPlayback}>
            Sync with Playback
          </button>
        </div>
      )}
      {wideTranscript ? (
        <WideTranscript
          setTimeSeconds={setTimeSeconds}
          transcript={transcript}
          speakers={speakersByNumber}
          sessionUser={sessionUser}
          matches={matches}
          activeTopics={activeTopics}
          hidden={hideTranscript}
          callState={callState}
          currentTime={currentTime}
          setTranscriptRef={setTranscriptRef}
        />
      ) : (
        <NarrowTranscript
          setTimeSeconds={setTimeSeconds}
          hidden={hideTranscript}
          transcript={transcript}
          speakers={speakers}
          matches={matches}
          activeTopics={activeTopics}
          callState={callState}
          currentTime={currentTime}
          setTranscriptRef={setTranscriptRef}
        />
      )}
    </div>
  );
};

function jumpToTime(time: Seconds, transcript: List<TranscriptBlock>) {
  for (const transcriptLine of transcript) {
    if (time >= transcriptLine.startTime && time < transcriptLine.endTime) {
      scrollTranscriptToTime(transcriptLine.startTime);
      return;
    }
  }
}
